import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tag, ChevronRight, ShoppingCart, Heart, Sparkles, Flame } from 'lucide-react';
import axiosInstance from '../api/axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import useCartStore from '../store/cartStore';
import useWishlistStore from '../store/wishlistStore';
import SaleCountdown from '../components/SaleCountdown';
import { imgVariant } from '../utils/imgUrl';
import { formatEUR } from '../utils/format';

/**
 * Öffentliche SALE-Seite — zeigt alle Artikel mit `sale_price` < `price`,
 * automatisch gruppiert nach Hauptkategorie und Unterkategorie.
 *
 * Layout:
 *  - Großer Header „SALE" mit max. Rabatt-Anzeige
 *  - Quick-Nav als Kategorie-Chips (sticky on mobile)
 *  - Pro Kategorie: Card-Block mit Untergruppen als Sub-Überschriften
 *  - Jede Produkt-Card zeigt: Bild, Name, durchgestrichener Originalpreis,
 *    fetter Sale-Preis, Rabatt-Badge
 */

const formatPrice = (n) => `${formatEUR(Number(n || 0))}`;

const SalePage = () => {
  const [data, setData] = useState({ total: 0, categories: [] });
  const [loading, setLoading] = useState(true);
  const addItem = useCartStore((s) => s.addItem);
  const toggleWishlist = useWishlistStore((s) => s.toggleWishlist);
  const isInWishlist = useWishlistStore((s) => s.isInWishlist);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await axiosInstance.get('/products/sale');
        setData(r.data || { total: 0, categories: [] });
      } catch (e) {
        toast.error('SALE-Bereich konnte nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const maxDiscount = data.categories.reduce(
    (m, c) => Math.max(m, c.max_discount_pct || 0), 0
  );

  const handleAddToCart = (p) => {
    const finalPrice = p.sale_price && p.sale_price < p.price ? p.sale_price : null;
    if (!addItem(p, 1, null, finalPrice)) return;
    toast.success(`„${p.name}" zum Warenkorb hinzugefügt`);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5EDE4' }} data-testid="sale-page">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#B0512E] via-[#A04A2A] to-[#8B3D24] text-white">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4), transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.3), transparent 40%)',
        }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/15 backdrop-blur-sm text-xs tracking-[4px] uppercase mb-4">
            <Flame className="h-3.5 w-3.5" /> Limited Sale · Spare jetzt
          </div>
          <h1 className="text-5xl sm:text-7xl font-bold mb-3" style={{ fontFamily: 'Georgia, serif', letterSpacing: '-2px' }}>
            SALE
          </h1>
          {maxDiscount > 0 && (
            <p className="text-lg sm:text-xl text-white/90">
              Bis zu <span className="font-bold text-[#FFE4A3]">−{maxDiscount}%</span> auf ausgewählte Artikel
            </p>
          )}
          {!loading && (
            <p className="text-sm text-white/70 mt-2">
              {data.total} {data.total === 1 ? 'reduzierter Artikel' : 'reduzierte Artikel'}
              {data.categories.length > 0 && ` in ${data.categories.length} ${data.categories.length === 1 ? 'Kategorie' : 'Kategorien'}`}
            </p>
          )}
        </div>
      </div>

      {/* Kategorie-Schnellzugriff */}
      {!loading && data.categories.length > 0 && (
        <div className="sticky top-0 z-30 bg-[#F5EDE4]/95 backdrop-blur-sm border-b border-[#D4C4B0] shadow-sm">
          <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 flex gap-2 overflow-x-auto" data-testid="sale-category-nav">
            {data.categories.map((c) => (
              <a
                key={c.category}
                href={`#cat-${c.category.replace(/\s+/g, '-')}`}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium whitespace-nowrap bg-white text-[#5D4E3C] border border-[#C4A084] hover:bg-[#C4A084] hover:text-white transition-colors"
                data-testid={`sale-nav-${c.category}`}
              >
                <Tag className="h-3 w-3" />
                {c.category}
                <span className="text-[10px] opacity-70">({c.count})</span>
                {c.max_discount_pct > 0 && (
                  <span className="text-[10px] bg-[#B0512E] text-white px-1 py-px ml-1">−{c.max_discount_pct}%</span>
                )}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Inhalt */}
      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-8 sm:py-12">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B7355]"></div>
          </div>
        ) : data.categories.length === 0 ? (
          <div className="text-center py-16">
            <Sparkles className="h-16 w-16 mx-auto mb-4 text-[#C4A084]" />
            <h2 className="text-xl font-bold text-[#5D4E3C]" style={{ fontFamily: 'Georgia, serif' }}>
              Aktuell keine SALE-Artikel
            </h2>
            <p className="text-sm text-[#8B7355] mt-2">
              Schau bald wieder vorbei – wir bereiten Saison-Reduktionen vor.
            </p>
            <Link to="/produkte">
              <Button className="mt-5 text-xs">
                Zum Shop <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-12">
            {data.categories.map((cat) => (
              <section
                key={cat.category}
                id={`cat-${cat.category.replace(/\s+/g, '-')}`}
                className="scroll-mt-20"
                data-testid={`sale-cat-${cat.category}`}
              >
                {/* Kategorie-Header */}
                <div className="flex items-end justify-between gap-4 mb-5 pb-3 border-b-2 border-[#C4A084]">
                  <div>
                    <p className="text-[10px] tracking-[3px] uppercase text-[#8B7355]">Kategorie</p>
                    <h2
                      className="text-2xl sm:text-3xl font-bold text-[#3D2F22]"
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      {cat.category}
                    </h2>
                    <p className="text-xs text-[#8B7355]">{cat.count} {cat.count === 1 ? 'Artikel' : 'Artikel'} reduziert</p>
                  </div>
                  {cat.max_discount_pct > 0 && (
                    <span className="px-3 py-1.5 bg-[#B0512E] text-white text-sm font-bold tracking-wider">
                      bis −{cat.max_discount_pct}%
                    </span>
                  )}
                </div>

                {/* Sub-Gruppen */}
                {cat.subcategories.map((sub) => (
                  <div key={sub.subcategory || '__none__'} className="mb-8">
                    {sub.subcategory && (
                      <div className="flex items-center gap-2 mb-3">
                        <ChevronRight className="h-4 w-4 text-[#C4A084]" />
                        <h3 className="text-sm font-semibold text-[#5D4E3C] uppercase tracking-wider">
                          {sub.subcategory}
                        </h3>
                        <span className="text-xs text-[#8B7355]">({sub.count})</span>
                      </div>
                    )}

                    {/* Produkt-Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                      {sub.items.map((p) => (
                        <div
                          key={p.id}
                          className="group relative bg-white border border-[#D4C4B0] hover:border-[#C4A084] transition-colors flex flex-col"
                          data-testid={`sale-product-${p.id}`}
                        >
                          {/* Rabatt-Badge */}
                          {p.discount_pct > 0 && (
                            <div className="absolute top-2 left-2 z-10 px-2 py-0.5 bg-[#B0512E] text-white text-xs font-bold">
                              −{p.discount_pct}%
                            </div>
                          )}
                          {/* Wishlist */}
                          <button
                            type="button"
                            onClick={() => toggleWishlist(p.id)}
                            className="absolute top-2 right-2 z-10 p-1.5 bg-white/95 hover:bg-white border border-[#D4C4B0] transition-colors"
                            title="Auf Wunschliste"
                          >
                            <Heart className={`h-3.5 w-3.5 ${isInWishlist(p.id) ? 'fill-[#B0512E] text-[#B0512E]' : 'text-[#8B7355]'}`} />
                          </button>

                          <Link to={`/produkt/${p.id}`} className="block">
                            <div className="aspect-square bg-[#FAF5EE] overflow-hidden">
                              {p.images?.[0] ? (
                                <img
                                  src={imgVariant(p.images[0], { w: 400 })}
                                  alt={p.name}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-[#C4A084]">
                                  <Tag className="h-12 w-12" />
                                </div>
                              )}
                            </div>
                          </Link>

                          <div className="p-3 flex-1 flex flex-col">
                            <Link to={`/produkt/${p.id}`} className="flex-1">
                              <h4 className="text-sm font-medium text-[#3D2F22] line-clamp-2 group-hover:text-[#5D4E3C]">
                                {p.name}
                              </h4>
                            </Link>
                            <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                              <span className="text-base font-bold text-[#B0512E]">
                                {formatPrice(p.sale_price)}
                              </span>
                              <span className="text-xs text-[#8B7355] line-through">
                                {formatPrice(p.price)}
                              </span>
                            </div>
                            {p.sale_ends_at && (
                              <SaleCountdown
                                targetIso={p.sale_ends_at}
                                className="text-[#B0512E] mt-1"
                                prefix="Endet in"
                              />
                            )}
                            <Button
                              onClick={() => handleAddToCart(p)}
                              disabled={!(p.stock > 0)}
                              size="sm"
                              className="w-full mt-2 text-xs"
                              data-testid={`sale-add-to-cart-${p.id}`}
                            >
                              <ShoppingCart className="h-3 w-3 mr-1" />
                              {!(p.stock > 0) ? 'Ausverkauft' : 'In den Warenkorb'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SalePage;
